import { TranslateService } from "@ngx-translate/core";

import { Observable, Subscription } from "rxjs";
import { skip } from "rxjs/operators";
import { select, Store } from "@ngrx/store";
import {
  AppState,
  movementsExcelListState,
  movementsStateIsLoading,
  movementStateList,
  movementsTotalSizeState,
} from "src/app/store/reducers";

import {
  ColumnSorterInfo,
  ConfigScope,
  ConfigSettingType,
  ExportEvent,
  FieldType,
  FormatType,
  SortEvent,
  TableConfig,
  TableEvent,
} from "src/app/models/custom-table/custom-table.model";
import { MovementDTO, MovementTypeCode } from "src/app/models/movements/movements";
import {
  getMovementTableConfig,
  saveMovementTableConfig,
} from "src/app/store/actions/movements/movements.actions";
import { CHIP_STYLE, getChipStyle } from "../../utils/customTableUtils";

export class BaseMovementsList {
  initialSort: SortEvent = {
    active: "debitMovement.posDate",
    direction: "DESC",
  };

  isLoading$: Observable<boolean>;

  columnInfo$: Observable<ColumnSorterInfo[]>;

  movementsList: MovementDTO[];
  movementsList$: Observable<MovementDTO[]>;
  excelMovementsList$: Observable<MovementDTO[]>;
  totalMovements$: Observable<number>;

  movementsListSub: Subscription;

  formPath: string = "movements/movement";
  tableTitle: string;
  headerIcon: string;

  tableConfig: TableConfig[] = [
    {
      header: "Movements.posDate",
      field: "debitMovement.posDate",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME,
      dateFilter: true,
      noOffset: true,
    },
    {
      header: "Movements.transactionID",
      field: "transactionIdentifier",
      fieldType: FieldType.STRING,
      hidden: true,
      identifier: true,
    },
    {
      header: "Movements.code",
      field: "code",
      fieldType: FieldType.STRING,
      hidden: true,
      searchable: true,
    },
    {
      header: "Movements.siteCode",
      field: "debitMovement.siteCode",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Movements.pumpNumber",
      field: "debitMovement.pumpNumber",
      fieldType: FieldType.NUMBER,
      hidden: true,
    },
    {
      header: "Movements.posCode",
      field: "debitMovement.posCode",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Movements.siteDescription",
      field: "debitMovement.siteDescription",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "Movements.movementType",
      field: "movementTypeDescription",
      fieldType: FieldType.CHIP_STRING,
    },
    {
      header: "Movements.productDescription",
      field: "debitMovement.productDescription",
      fieldType: FieldType.CHIP_STRING,
    },
    {
      header: "Movements.productCode",
      field: "debitMovement.productCode",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Movements.productUnitPrice",
      field: "debitMovement.productUnitPrice",
      fieldType: FieldType.NUMBER,
      formatType: FormatType.FUELUNITPRICE,
      hidden: true,
    },
    {
      header: "Movements.amount",
      field: "amount",
      fieldType: FieldType.NUMBER,
      formatType: FormatType.AMOUNT,
      style: "font-weight: 600;",
      strikethroughZeros: true,
    },
    {
      header: "Movements.volume",
      field: "volume",
      fieldType: FieldType.NUMBER,
      formatType: FormatType.UOM,
      style: "font-weight: 600;",
      strikethroughZeros: true,
    },
    {
      header: "Movements.contractCode",
      field: "contract.code",
      fieldType: FieldType.STRING,
      searchable: true,
      hidden: true,
    },
    {
      header: "Movements.contractDescription",
      field: "contract.description",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Movements.vatin",
      field: "contract.customer.vatin",
      fieldType: FieldType.STRING,
    },
    {
      header: "Movements.customerIdentifier",
      field: "contract.customer.code",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Movements.customerName",
      field: "contract.customer.name",
      fieldType: FieldType.STRING,
    },
    {
      header: "Movements.customerStatus",
      field: "contract.customer.statusDescription",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Movements.usedCards",
      field: "debitMovement.n_cards",
      fieldType: FieldType.NUMBER,
      hidden: true,
    },
    {
      header: "Movements.card0Code",
      field: "debitMovement.card[1].code",
      fieldType: FieldType.ICON,
      searchable: true,
    },

    {
      header: "Movements.card0Number",
      field: "debitMovement.card[1].cardNumber",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Movements.card0Pan",
      field: "debitMovement.card[1].pan",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Movements.card0LayoutDescription",
      field: "debitMovement.card[1].cardLayoutDescription",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Movements.card0SystemDescription",
      field: "debitMovement.card[1].cardSystemDescription",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Movements.card0Status",
      field: "debitMovement.card[1].cardStatusDescription",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Movements.card0PromptData",
      field: "debitMovement.card[1].promptData",
      fieldType: FieldType.OBJECT,
      searchable: true,
    },
    {
      header: "Movements.card1Code",
      field: "debitMovement.card[2].code",
      fieldType: FieldType.ICON,
      searchable: true,
    },
    {
      header: "Movements.card1Number",
      field: "debitMovement.card[2].cardNumber",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Movements.card1Pan",
      field: "debitMovement.card[2].pan",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Movements.card1LayoutDescription",
      field: "debitMovement.card[2].cardLayoutDescription",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Movements.card1SystemDescription",
      field: "debitMovement.card[2].cardSystemDescription",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Movements.card1Status",
      field: "debitMovement.card[2].cardStatusDescription",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Movements.card1PromptData",
      field: "debitMovement.card[2].promptData",
      fieldType: FieldType.OBJECT,
      searchable: true,
    },
    {
      header: "Movements.vehicleCode",
      field: "debitMovement.vehicle.code",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Movements.vehicleDescription",
      field: "debitMovement.vehicle.description",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Movements.vehicleLicensePlate",
      field: "debitMovement.vehicle.licensePlate",
      fieldType: FieldType.CHIP_STRING,
      searchable: true,
    },
    {
      header: "Movements.vehicleOdometer",
      field: "debitMovement.vehicle.odometer",
      fieldType: FieldType.NUMBER,
    },
    {
      header: "Movements.vehicleWorkingHours",
      field: "debitMovement.vehicle.workingHours",
      fieldType: FieldType.NUMBER,
    },
    {
      header: "Movements.vehicleAverageCalcAverage",
      field: "debitMovement.vehicle.averageCalcValue",
      fieldType: FieldType.CHIP_NUMBER,
    },
    {
      header: "Movements.vehicleAverageCalcDeviation",
      field: "debitMovement.vehicle.averageCalcDeviation",
      fieldType: FieldType.NUMBER,
      hidden: true,
    },
    {
      header: "Movements.vehicleAverageEstimated",
      field: "debitMovement.vehicle.averageEstimated",
      fieldType: FieldType.NUMBER,
      hidden: true,
    },
    {
      header: "Movements.vehicleAverageDeviation",
      field: "debitMovement.vehicle.averageDeviation",
      fieldType: FieldType.NUMBER,
      hidden: true,
    },
    {
      header: "Movements.CreatedOn",
      field: "createdOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME,
      hidden: true,
    },
    {
      header: "Movements.employeeCode",
      field: "debitMovement.employee.employeeCode",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Movements.employeeName",
      field: "debitMovement.employee.name",
      fieldType: FieldType.STRING,
    },
    {
      header: "Movements.employeeVatin",
      field: "debitMovement.employee.vatin",
      fieldType: FieldType.STRING,
      hidden: true,
    },
  ];

  constructor(
    public store: Store<AppState>,
    private translate: TranslateService
  ) {
    localStorage.removeItem("movementCode");
    localStorage.setItem("movementTypeDebit", 'movementTypeCode=like="DEBIT"');

    this.isLoading$ = this.store.pipe(select(movementsStateIsLoading));
    this.movementsList$ = this.store.pipe(select(movementStateList));
    this.excelMovementsList$ = this.store.pipe(select(movementsExcelListState));
    this.totalMovements$ = this.store.pipe(select(movementsTotalSizeState));
  }

  setTranslatedTitle() {
    this.translate
      .get("Tabs.Transactions")
      .subscribe((translatedTitle) => (this.tableTitle = translatedTitle));
  }

  setTableCustomElements() {
    this.movementsListSub = this.movementsList$
      .pipe(skip(1))
      .subscribe((movements: any[]) => {
        this.movementsList = movements;

        if (movements) {
          this.movementsList = this.movementsList.map((movement) => {
            let processedMovement = movement;
            processedMovement = this.setCustomFields(processedMovement);
            return processedMovement;
          });
        }
      });
  }

  setCustomFields(movement: any) {
    const defaultMovement = {
      ...movement,
    };

    const { movementTypeCode, movementTypeDescription, debitMovement } = movement;

    if (debitMovement == null) {
      return defaultMovement;
    }

    const { productDescription, productColor } = debitMovement;
    const card0Default = debitMovement?.card[1];
    const card1Default = debitMovement?.card[2];
    const vehicleDefault = debitMovement?.vehicle;

    let movementType = null;
    let productDescriptionData = null;
    let card0CodeData = null;
    let card1CodeData = null;
    let reportedOdometerData = null;
    let reportedWorkingHoursData = null;
    let vehicleLicensePlateData = null;
    let vehicleAverageData = null;

    if (movementTypeCode) {
      switch (movementTypeCode) {
        case MovementTypeCode.DEBIT:
          movementType = {
            value: movementTypeDescription,
            icon: "remove_circle_outline",
            style: "background-color: rgb(255, 150, 150);",
          };
          break;
        case MovementTypeCode.CREDIT:
          movementType = {
            value: movementTypeDescription,
            icon: "add_circle_outline",
            style: "background-color: rgb(208 241 203);",
          };
          break;
        default:
          break;
      }
    }

    if (productDescription) {
      var color = productColor ? productColor : "ddd";
      productDescriptionData = {
        value: productDescription,
        icon: "local_gas_station",
        style: `background-color: #${color}; color: #FFF;`,
      };
    }

    if (card0Default) {
      card0CodeData = {
        value: card0Default.code,
        icon: "credit_card",
      };

      if (card1Default) {
        card1CodeData = {
          value: card1Default.code,
          icon: "credit_card",
        };
      }
    }

    if (vehicleDefault) {
      vehicleLicensePlateData = {
        value: vehicleDefault.licensePlate,
        icon: "directions_car",
      };

      if (vehicleDefault.odometer) {
        reportedOdometerData = {
          value: vehicleDefault.odometer,
          icon: "speed",
        };
      }

      if (vehicleDefault.workingHours) {
        reportedWorkingHoursData = {
          value: vehicleDefault.workingHours,
          icon: "pending_actions",
        };
      }

      if (vehicleDefault.averageCalcValue) {
        vehicleAverageData = {
          value: vehicleDefault.averageCalcValue,
        };

        if (
          vehicleDefault?.averageCalcDeviation >
          vehicleDefault?.averageDeviation
        ) {
          vehicleAverageData.icon = "priority_high";
          vehicleAverageData.style = "background-color: #F00; color: #FFF;";
          vehicleAverageData.tooltip = this.translate.instant(
            "Movements.vehicleAverageAbove"
          );
        } else if (vehicleDefault?.averageCalcDeviation < 0) {
          let successStyle = getChipStyle(CHIP_STYLE.SUCCESS);
          vehicleAverageData.icon = "check";
          vehicleAverageData.style = successStyle.style;
          vehicleAverageData.tooltip = this.translate.instant(
            "Movements.vehicleAverageBetter"
          );
        } else {
          let successStyle = getChipStyle(CHIP_STYLE.INACTIVE);
          vehicleAverageData.style = successStyle.style;
          vehicleAverageData.tooltip = this.translate.instant(
            "Movements.vehicleAverageExpected"
          );
        }
      }
    }

    return {
      ...movement,
      movementTypeDescription: movementType,
      debitMovement: {
        ...debitMovement,
        productDescription: productDescriptionData,
        card: {
          1: { ...card0Default, code: card0CodeData },
          2: { ...card1Default, code: card1CodeData },
        },
        vehicle: {
          ...vehicleDefault,
          licensePlate: vehicleLicensePlateData,
          averageCalcValue: vehicleAverageData,
        },
      },
    };
  }

  getTableConfig(usageCode: string) {
    this.store.dispatch(
      getMovementTableConfig({
        options: {
          code: usageCode,
          settingType: ConfigSettingType.TABLE_CONFIG,
        },
      })
    );
  }

  getTableData(tableEvent: TableEvent, getMovementsList: Function) {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = tableEvent;
    getMovementsList(
      pageEvent,
      sortEvent ?? this.initialSort,
      searchEvent,
      filterEvent
    );
  }

  getExportData(exportEvent: ExportEvent, getMovementsExcelList: Function) {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = exportEvent;
    getMovementsExcelList(pageEvent, sortEvent ?? this.initialSort, searchEvent, filterEvent);
  }

  saveTableConfig(tableColumnInfo: ColumnSorterInfo[], usageCode: string) {
    this.store.dispatch(
      saveMovementTableConfig({
        config: {
          code: usageCode,
          settingTypeCode: ConfigSettingType.TABLE_CONFIG,
          scope: ConfigScope.USER,
          value: { tableColumnInfo },
        },
      })
    );
  }
}
